export interface ProjectData {
    title: string;
    description: string;
    challenge: string;
    solution: string;
    features: string[];
    technologies: string[];
    results: string[];
    image: string;
    images?: string[]; // Additional images for projects with multiple screenshots
    projectType: 'web' | 'mobile'; // Determines layout style
    categories: string[];
    clientTestimonial?: {
        quote: string;
        author: string;
        position: string;
    };
}

export const projectsData: Record<string, ProjectData> = {
    'pel-church-platform': {
        title: 'Complete Digital Ministry Solution',
        description: 'A comprehensive church platform that revolutionizes how ministries connect with their congregation through integrated streaming, CRM, and engagement tools.',
        challenge: 'Churches needed a unified platform to manage their congregation, stream services, handle donations, and provide educational content. Traditional solutions were fragmented, requiring multiple platforms that didn\'t integrate well together.',
        solution: 'We developed PEL, an all-in-one church platform that seamlessly integrates CRM, live streaming, payment processing, learning management, and community engagement tools into a single, user-friendly ecosystem.',
        features: [
            'Advanced CRM system for member management and communication',
            'Live streaming with FFMPEG and Icecast integration for professional broadcasting',
            'Real-time chat module for interactive services',
            'Integrated media player with playlist management',
            'Secure payment gateway for tithes and offerings (Card & Mobile Money)',
            'Email automation with custom sender IDs',
            'Comprehensive visitor analytics and engagement tracking',
            'Learning Management System with paid and free courses',
            'Multi-platform rebroadcasting to Facebook, YouTube, and Twitch',
            'Content scheduling and automated publishing',
            'Role-based access control for staff and volunteers'
        ],
        technologies: ['React', 'Node.js', 'FFMPEG', 'Icecast', 'WebRTC', 'Payment APIs', 'Email APIs', 'Social Media APIs'],
        results: [
            'Increased online engagement by 300% during services',
            'Streamlined donation processing with 95% success rate',
            'Reduced administrative overhead by 60%',
            'Enhanced member communication and retention',
            'Successfully scaled to support 10,000+ concurrent viewers'
        ],
        image: 'portifolio/pel-church-platform.png',
        projectType: 'web',
        categories: ['Faith-Based Tech', 'Streaming & CRM', 'Payment Integration'],
        clientTestimonial: {
            quote: 'The PEL platform transformed our ministry. We\'ve seen unprecedented engagement and our ability to reach people has grown exponentially.',
            author: 'Pastor David Johnson',
            position: 'Senior Pastor, New Life Church'
        }
    },
    'buildmate-construction': {
        title: 'Construction Project Management Platform',
        description: 'A specialized platform designed for overseas construction projects, providing transparent cost estimation, verified contractor networks, and material sourcing solutions for clients building from abroad.',
        challenge: 'People looking to build especially those abroad struggled with getting clear estimates from contractors, reliable companies to work with, a place to source materials, and to save for their constructions projects.',
        solution: 'We developed BuildMate to bridge the gap between overseas clients and local construction markets, offering transparent cost breakdowns, verified contractor profiles, integrated savings plans, and reliable material sourcing to eliminate construction uncertainties.',
        features: [
            'Transparent cost estimation with detailed breakdowns for overseas clients',
            'Verified contractor network with ratings, reviews, and portfolio verification',
            'Material sourcing marketplace with price comparisons and quality assurance',
            'Construction savings planner with milestone-based payment protection',
            'Virtual project monitoring with real-time photo and video updates',
            'Currency conversion and international payment processing',
            'Local market insights and construction regulation guidance',
            'Remote project management dashboard for overseas supervision',
            'Progress tracking with milestone approvals and fund release',
            'Communication bridge between overseas clients and local teams',
            'Document management for permits, contracts, and certifications'
        ],
        technologies: ['React', 'Node.js', 'React Native', 'PostgreSQL', 'AWS', 'Payment APIs', 'Currency APIs', 'Video Streaming'],
        results: [
            'Reduced construction cost uncertainty by 60% for overseas clients',
            'Connected overseas clients with 500+ verified local contractors',
            'Facilitated secure savings and payments for £8M+ in projects',
            'Achieved 95% client satisfaction through transparent processes',
            'Reduced project communication barriers by 80% across time zones'
        ],
        image: 'images/portifolio/buildmate.png',
        projectType: 'web',
        categories: ['Construction Tech', 'Project Management', 'Marketplace Platform'],
        clientTestimonial: {
            quote: 'BuildMate gave me confidence to build my dream home in Nigeria from the UK. The transparent pricing and verified contractors made all the difference.',
            author: 'James Mitchell',
            position: 'Overseas Property Developer'
        }
    },
    'wot-tv-platform': {
        title: '24/7 Online Television Broadcasting Platform',
        description: 'A sophisticated online television platform delivering 24-hour programming with professional broadcasting capabilities powered by advanced media streaming technology.',
        challenge: 'Creating a reliable, scalable online TV platform that could handle continuous broadcasting, manage complex programming schedules, and deliver high-quality content to viewers worldwide.',
        solution: 'We built WOT TV using LiquidSoap for robust audio/video streaming, combined with a custom content management system that handles programming schedules, content delivery, and viewer analytics.',
        features: [
            '24/7 continuous broadcasting capability',
            'LiquidSoap-powered backend for professional audio/video streaming',
            'Advanced programming scheduler with automatic content switching',
            'Content management system for media uploads and organization',
            'Multi-bitrate streaming for optimal viewing across devices',
            'Real-time viewer analytics and engagement metrics',
            'Automated advertising insertion and sponsorship management',
            'Mobile-responsive web interface',
            'Content moderation and quality control systems',
            'Backup streaming infrastructure for 99.9% uptime'
        ],
        technologies: ['LiquidSoap', 'FFMPEG', 'HLS Streaming', 'React', 'Node.js', 'CDN Integration', 'MongoDB'],
        results: [
            'Achieved 99.9% uptime with zero broadcast interruptions',
            'Scaled to support 50,000+ concurrent viewers',
            'Reduced content management time by 70%',
            'Implemented automated programming reducing manual intervention by 90%',
            'Delivered high-quality streaming across multiple devices and connection speeds'
        ],
        image: 'portifolio/wot-tv-platform.png',
        projectType: 'web',
        categories: ['Media Streaming', 'Live Broadcasting', 'Content Management'],
        clientTestimonial: {
            quote: 'WOT TV has exceeded our expectations. The platform is incredibly reliable and the automated programming features have saved us countless hours.',
            author: 'Sarah Mitchell',
            position: 'Content Director, WOT Media'
        }
    },
    'school-of-divinity-lms': {
        title: 'Advanced Learning Management System',
        description: 'A comprehensive LMS designed for theological education with advanced course management, student tracking, and monetization capabilities built on WordPress.',
        challenge: 'Educational institutions needed a robust learning platform that could handle both free and paid courses, track student progress, and provide detailed analytics while maintaining ease of use for instructors.',
        solution: 'We developed a sophisticated WordPress-based LMS with custom plugins for course monetization, advanced analytics, and seamless payment integration, specifically tailored for theological education.',
        features: [
            'Course creation and management system with multimedia support',
            'Integrated payment system for course monetization',
            'Student progress tracking and performance analytics',
            'Interactive assignments and assessment tools',
            'Certificate generation and credential management',
            'Instructor dashboard with comprehensive analytics',
            'Discussion forums and community features',
            'Mobile-responsive design for learning on any device',
            'Advanced reporting and institutional analytics',
            'Integration with external authentication systems',
            'Automated enrollment and course completion workflows'
        ],
        technologies: ['WordPress', 'Custom PHP Development', 'MySQL', 'Payment Gateway APIs', 'Learning Analytics'],
        results: [
            'Increased course completion rates by 45%',
            'Generated 250% ROI through course monetization',
            'Reduced administrative workload by 55%',
            'Improved student satisfaction scores to 4.8/5',
            'Successfully onboarded 5,000+ students across 200+ courses'
        ],
        image: 'portifolio/school-of-divinity-lms.png',
        images: [
            'portifolio/school-of-divinity-lms.png',
            'portifolio/school-of-divinity-dashboard.png'
        ],
        projectType: 'web',
        categories: ['E-Learning', 'Content Monetization', 'Analytics & Tracking'],
        clientTestimonial: {
            quote: 'This LMS has revolutionized our educational delivery. The analytics help us understand our students better and the monetization features have opened new revenue streams.',
            author: 'Dr. Michael Thompson',
            position: 'Academic Director, School of Divinity'
        }
    },
    'wip-prayer-platform': {
        title: 'Community Prayer Platform',
        description: 'An innovative Christian platform designed for communion prayers featuring real-time communication capabilities through WebRTC technology.',
        challenge: 'Creating a spiritual community platform that could facilitate real-time prayer sessions, handle multiple participants, and maintain the sacred nature of prayer gatherings in a digital environment.',
        solution: 'We developed WIP using WebRTC for seamless real-time communication, combined with community features that preserve the intimacy and spiritual connection of traditional prayer gatherings.',
        features: [
            'WebRTC-powered real-time video and audio communication',
            'Private and group prayer room creation',
            'Prayer request submission and sharing system',
            'Community prayer calendar and scheduling',
            'Meditation and guided prayer sessions',
            'Prayer journal and personal reflection tools',
            'Anonymous prayer request options for sensitive matters',
            'Multi-participant prayer circles with up to 50 people',
            'Session recording for personal reflection (with permission)',
            'Mobile app for prayer on-the-go',
            'Integration with church calendars and events'
        ],
        technologies: ['WebRTC', 'React Native', 'Node.js', 'Socket.io', 'MongoDB', 'Push Notifications'],
        results: [
            'Connected over 2,000 prayer warriors globally',
            'Facilitated 10,000+ prayer sessions',
            'Achieved 95% user retention rate',
            'Reduced technical barriers to spiritual community participation',
            'Created a safe space for vulnerable prayer sharing'
        ],
        image: 'portifolio/wip-prayer-platform.png',
        projectType: 'web',
        categories: ['WebRTC Integration', 'Community Building', 'Real-Time Communication'],
        clientTestimonial: {
            quote: 'WIP has brought our prayer community closer together than ever before. The technology feels invisible, allowing us to focus on what truly matters - prayer and fellowship.',
            author: 'Elder Mary Rodriguez',
            position: 'Prayer Ministry Leader'
        }
    },
    'disciple-link-messaging': {
        title: 'Intelligent Bulk Messaging Solution',
        description: 'A sophisticated mobile application for personalized bulk messaging with carrier integration, smart scheduling, and comprehensive analytics.',
        challenge: 'Organizations needed a way to send personalized bulk messages through carrier services while maintaining personal touch, ensuring delivery, and managing complex scheduling requirements.',
        solution: 'We created Disciple Link, a mobile app that integrates directly with carrier messaging services, provides advanced personalization, and offers intelligent scheduling capabilities.',
        features: [
            'Personalized bulk messaging with dynamic content insertion',
            'Direct carrier integration for reliable message delivery',
            'Advanced scheduling system with timezone awareness',
            'Contact management with segmentation capabilities',
            'Message template library with customization options',
            'Delivery tracking and read receipts',
            'A/B testing for message optimization',
            'Automated follow-up sequences',
            'Compliance management for messaging regulations',
            'Multi-language support for global reach',
            'Integration with CRM systems and databases'
        ],
        technologies: ['React Native', 'Carrier APIs', 'Node.js', 'MongoDB', 'Push Notifications', 'Analytics SDKs'],
        results: [
            'Achieved 98% message delivery rate',
            'Increased engagement by 200% through personalization',
            'Reduced message sending time by 85%',
            'Saved organizations $50,000+ annually in communication costs',
            'Successfully delivered over 1 million personalized messages'
        ],
        image: 'portifolio/disciple-link-messaging.png',
        images: [
            'portifolio/disciple-link-messaging.png',
            'portifolio/disciple-link-dashboard.png',
            'portifolio/disciple-link-analytics.png',
            'portifolio/disciple-link-scheduling.png'
        ],
        projectType: 'mobile',
        categories: ['Mobile App', 'SMS Automation', 'Personalization Engine']
    },
    'beyondpips-trading': {
        title: 'BeyondPips Automated Gold Trading Platform',
        description: 'A mobile app for an automated gold trading service that lets traders monitor performance, manage broker accounts, and track commissions — while a trading bot handles the actual execution on their behalf.',
        challenge: 'BeyondPips needed a way for their traders to keep tabs on how their automated gold trading bot was performing across multiple broker accounts. There was no easy way to see overall P&L, check which accounts were live, manage subscriptions, or understand commission breakdowns — everything was scattered across different platforms.',
        solution: 'We built a mobile app that pulls everything into one place. Traders can see their real-time performance at a glance, monitor all connected broker accounts, download and update the trading bot, and get detailed commission reports broken down by month and broker. The app also handles subscription management and licence keys so everything stays organised.',
        features: [
            'Real-time dashboard showing P&L, win rate, and trade count',
            'Multi-broker account management with live status indicators',
            'Monthly commission tracking with per-trade breakdowns',
            'Expert Advisor bot download and update management',
            'Subscription and licence key handling',
            'Gold (XAUUSD) price alerts with push notifications',
            'Recent trade activity feed with individual profit/loss',
            'Light, auto, and dark theme options',
            'Two-factor authentication and active device management',
            'Balance and deposit history tracking'
        ],
        technologies: ['React Native', 'Node.js', 'MetaTrader APIs', 'Push Notifications', 'Payment APIs'],
        results: [],
        image: 'portifolio/beyondpips-dashboard.png',
        images: [
            'portifolio/beyondpips-dashboard.png',
            'portifolio/beyondpips-settings.png',
            'portifolio/beyondpips-commissions.png'
        ],
        projectType: 'mobile',
        categories: ['Forex Trading', 'Mobile App']
    },
    'beyondpips-web': {
        title: 'BeyondPips Web Platform & Admin Dashboard',
        description: 'The web side of BeyondPips — a marketing site that explains the service and onboards new traders, paired with a full admin dashboard for managing trading instances, licences, commissions, and users.',
        challenge: 'BeyondPips needed a public-facing website to attract and convert new traders, along with an internal admin platform where the team could manage trading instances across multiple brokers, track revenue, handle subscriptions, and monitor the performance of every connected account.',
        solution: 'We built a clean marketing website with a clear onboarding flow, and a comprehensive admin dashboard that gives the BeyondPips team full visibility into their platform — from live trading instances and commission tracking to user management and bot version control.',
        features: [
            'Marketing website with feature showcase and pricing plans',
            'Invitation-only registration with referral system',
            'Secure login with scripture-inspired branding',
            'Admin dashboard with real-time platform analytics',
            'Trading instance management across multiple brokers',
            'Net profit and win rate tracking per instance',
            'Revenue and commission reporting',
            'Subscription and licence management',
            'User management with role-based access',
            'Bot version control and distribution',
            'Coupon and discount management',
            'Email and SMS notification system'
        ],
        technologies: ['React', 'Node.js', 'PostgreSQL', 'MetaTrader APIs', 'Payment APIs', 'Email APIs'],
        results: [],
        image: 'portifolio/beyondpips-web-homepage.png',
        images: [
            'portifolio/beyondpips-web-homepage.png',
            'portifolio/beyondpips-web-features.png',
            'portifolio/beyondpips-web-login.png'
        ],
        projectType: 'web',
        categories: ['Forex Trading', 'Web Platform']
    },
    'prayer-community-app': {
        title: 'Prayer & Spiritual Growth App',
        description: 'A mobile app designed for personal spiritual development — bringing together prayer projects, sermon archives, notes, and community prayer groups into one thoughtful experience.',
        challenge: 'There was a need for christian communities to manage their prayer life, track their spiritual journey, access sermons on demand, and connect with others for group prayer — without relying on scattered tools like WhatsApp groups and note-taking apps.',
        solution: 'We created a focused mobile app that organises prayer into projects, tracks prayer streaks and answered prayers, provides a searchable sermon library, and enables community features — all designed to feel personal and intentional rather than overwhelming.',
        features: [
            'Prayer project creation with tags and categories',
            'Prayer journey dashboard with streaks, time tracking, and stats',
            'Answered prayer tracking with testimonies',
            'Sermon library with search, categories, and playback',
            'Personal notes and reflections',
            'Community prayer group discovery and joining',
            'Monthly progress reports on prayer activity',
            'Recurring and urgent prayer flagging',
            'Push notification reminders for prayer times',
            'Clean, distraction-free interface for focused prayer'
        ],
        technologies: ['React Native', 'Node.js', 'MongoDB', 'Push Notifications', 'Audio Streaming'],
        results: [],
        image: 'portifolio/prayer-app-projects.png',
        images: [
            'portifolio/prayer-app-projects.png',
            'portifolio/prayer-app-journey.png',
            'portifolio/prayer-app-sermons.png'
        ],
        projectType: 'mobile',
        categories: ['Faith-Based Tech', 'Mobile App']
    }
};